import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Eye, ExternalLink } from "lucide-react";

const SLUGS = [
  { slug: "privacy", label: "Confidentialité" },
  { slug: "terms", label: "CGU" },
  { slug: "cgv", label: "CGV" },
  { slug: "legal-notice", label: "Mentions légales" },
];

export default function AdminLegal() {
  const [pages, setPages] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [lang, setLang] = useState("fr");

  const load = async () => {
    const { data } = await api.get("/admin/legal");
    const map = {};
    for (const p of data.pages) map[p.slug] = p;
    setPages(map);
    setDrafts({});
  };
  useEffect(() => { load(); }, []);

  const v = (slug, key) => {
    const d = drafts[slug] || {};
    return key in d ? d[key] : (pages?.[slug]?.[key] ?? "");
  };
  const setV = (slug, key, val) => setDrafts((d) => ({ ...d, [slug]: { ...(d[slug] || {}), [key]: val } }));
  const dirty = (slug) => Boolean(drafts[slug] && Object.keys(drafts[slug]).length);

  const save = async (slug) => {
    if (!dirty(slug)) return;
    setSaving((s) => ({ ...s, [slug]: true }));
    try {
      await api.put(`/admin/legal/${slug}`, drafts[slug]);
      toast.success("Page mise à jour");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving((s) => ({ ...s, [slug]: false }));
    }
  };

  if (!pages) return <div className="font-mono text-sm text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="admin-legal-page">
      <div className="flex items-end justify-between border-b border-border pb-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// Legal</div>
          <h1 className="mt-1 font-display text-2xl font-black tracking-tight">Pages légales</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Markdown. Variables : <code className="font-mono">{`{{app_name}} {{legal_name}} {{address}} {{contact_email}} {{siret}} {{vat_number}} {{today}}`}</code>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant={lang === "fr" ? "default" : "outline"} className="rounded-sm" onClick={() => setLang("fr")}>FR</Button>
          <Button variant={lang === "en" ? "default" : "outline"} className="rounded-sm" onClick={() => setLang("en")}>EN</Button>
        </div>
      </div>

      <Tabs defaultValue="privacy" className="mt-6">
        <TabsList className="rounded-sm">
          {SLUGS.map((s) => (
            <TabsTrigger key={s.slug} value={s.slug} className="rounded-sm" data-testid={`legal-tab-${s.slug}`}>{s.label}</TabsTrigger>
          ))}
        </TabsList>

        {SLUGS.map((s) => (
          <TabsContent key={s.slug} value={s.slug} className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2 font-mono text-xs">
                <Switch checked={Boolean(v(s.slug, "is_published"))} onCheckedChange={(b) => setV(s.slug, "is_published", b)} data-testid={`legal-${s.slug}-published`} /> Publiée
              </Label>
              <a href={`/legal/${s.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-xs text-[#002FA7] hover:underline">
                <Eye className="h-3 w-3" /> Aperçu public <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase text-muted-foreground">Titre ({lang.toUpperCase()})</Label>
              <Input className="mt-1 rounded-sm" value={v(s.slug, `title_${lang}`)} onChange={(e) => setV(s.slug, `title_${lang}`, e.target.value)} data-testid={`legal-${s.slug}-title-${lang}`} />
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase text-muted-foreground">Contenu Markdown ({lang.toUpperCase()})</Label>
              <Textarea rows={24} className="mt-1 rounded-sm font-mono text-xs leading-relaxed" value={v(s.slug, `body_${lang}`)} onChange={(e) => setV(s.slug, `body_${lang}`, e.target.value)} data-testid={`legal-${s.slug}-body-${lang}`} />
            </div>
            <div className="flex justify-end">
              <Button disabled={!dirty(s.slug) || saving[s.slug]} onClick={() => save(s.slug)} className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2" data-testid={`legal-${s.slug}-save`}>
                <Save className="h-4 w-4" /> {saving[s.slug] ? "..." : "Enregistrer"}
              </Button>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
